class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.drawerClass = "wt-drawer-search";
    this.drawer = document.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--active`;
    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-header__search-trigger",
      ".wt-header__search__close",
      ".search-results__terms",
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);

    // Predictive search uses the shopper's raw query. Full search must use the
    // same storefront search syntax: a normal q value plus options[prefix]=last.
    // Do not send Admin API field syntax such as title:term* to /search.
    // Liquid applies the final title exact-vs-prefix filter. Exact SKU searches
    // are handled separately before navigating to the results page.
    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.searchProperties = "title,variants.sku";

    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.emptyAnnouncement = this.querySelector(".search-empty");
    this.searchResultTop = this.querySelector(".search-result-top");

    this.isVisibleClearButton = false;

    this.saveSuggestionMenuInDesignMode =
      this.saveSuggestionMenuInDesignMode.bind(this);
    this.setupEventListeners();

    this.toggleTabindexElements = [this.input, this.closeButton];

    this.suggestionMenuContent = this.querySelector(".search-result-top").cloneNode(true);
    this.timeOut = null;
    this.searchRequestId = 0;

    if (this.normalizeSearchPageQuery()) return;

    this.init();
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      setTabindex(this.toggleTabindexElements, "-1");
      setTabindex([this.mainTrigger], "0");
      this.isOpen = false;
      setTimeout(() => {
        this.mainTrigger.focus();
      }, 0);
    } else {
      this.setAttribute("open", "");
      setTabindex(this.toggleTabindexElements, "0");
      setTabindex([this.mainTrigger], "-1");
      this.input.focus();
      this.input.setSelectionRange(
        this.input.value.length,
        this.input.value.length,
      );
      this.isOpen = true;
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    const isPredictiveSearchAvailable = this.hidePredictiveSearch();
    if (!isPredictiveSearchAvailable) return;

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.clearButton.addEventListener("click", () => {
      this.input.value = "";
      this.clearButton.style.display = "none";
      this.isVisibleClearButton = false;
      this.clearResults();
    });

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (this.isOpen) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    });

    this.input.addEventListener("input", () => {
      if (this.input.value.length > 0 && !this.isVisibleClearButton) {
        this.clearButton.style.display = "flex";
        this.clearButton.setAttribute("tabindex", "0");
        this.isVisibleClearButton = true;
      } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
        this.clearButton.style.display = "none";
        this.clearButton.setAttribute("tabindex", "-1");
        this.isVisibleClearButton = false;
      }
    });

    if (this.isSearchResultsPage()) {
      document.addEventListener("facets:change", () => {
        clearTimeout(this.searchPageSyncTimeout);
        this.searchPageSyncTimeout = setTimeout(() => {
          if (this.currentScopedQuery && this.currentDisplayQuery) {
            this.updateSearchPageDisplay(
              this.currentScopedQuery,
              this.currentDisplayQuery,
            );
          }
        }, 1000);
      });
    }
  }

  hidePredictiveSearch() {
    // When predictive search is not available, redirect to the search page.
    const featuresTag = document.getElementById("shopify-features");
    if (featuresTag) {
      const featuresData = JSON.parse(featuresTag.textContent);
      if (featuresData.predictiveSearch === false) {
        const searchPageUrl = this.dataset.searchPageUrl;
        this.triggers().forEach((trigger) => {
          trigger.addEventListener("click", (e) => {
            e.preventDefault();
            window.location.href = searchPageUrl;
          });
        });
        return false;
      }
    }
    return true;
  }

  setupEventListeners() {
    const form = this.querySelector("form.store-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode,
      );
    }
  }

  saveSuggestionMenuInDesignMode() {
    if (this.body.classList.contains(this.activeOverlayBodyClass))
      this.drawer.classList.toggle(this.classDrawerActive);
  }

  getQuery() {
    return this.input.value.trim();
  }

  sanitizeSearchTerm(searchTerm) {
    return searchTerm
      .replace(/["\\]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  normalizeTitleSearchTerm(searchTerm) {
    return this.sanitizeSearchTerm(searchTerm)
      .replace(/[-\/,.()&:|_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  getPredictiveFallbackTerm(searchTerm) {
    const stopWords = new Set([
      "a",
      "an",
      "and",
      "at",
      "by",
      "for",
      "from",
      "in",
      "of",
      "on",
      "or",
      "the",
      "to",
      "with",
    ]);
    const normalized = this.normalizeTitleSearchTerm(searchTerm).toLowerCase();
    const words = normalized.split(" ").filter(Boolean);
    const meaningful = words.filter((word) => !stopWords.has(word));
    return (meaningful.length ? meaningful : words).join(" ");
  }

  buildPredictiveSearchUrl(searchTerm, fields = this.searchProperties) {
    const params = new URLSearchParams();
    params.set("q", searchTerm);
    params.set("resources[type]", "product");
    params.set("resources[limit]", "10");
    params.set("resources[options][fields]", fields);
    params.set("resources[options][unavailable_products]", "last");
    params.set("section_id", "predictive-search");
    return `${routes.predictive_search_url}?${params.toString()}`;
  }

  async fetchPredictiveSection(searchTerm, fields = this.searchProperties) {
    const response = await fetch(this.buildPredictiveSearchUrl(searchTerm, fields));
    if (!response.ok) throw new Error(response.status);

    const text = await response.text();
    const section = new DOMParser()
      .parseFromString(text, "text/html")
      .querySelector("#shopify-section-predictive-search");

    if (!section) throw new Error("Missing predictive search section");
    return section;
  }

  buildTitleScopedQuery(searchTerm) {
    // Kept under the existing method name to avoid touching callers. For the
    // storefront /search endpoint this now returns the canonical shopper query,
    // not Admin API title:... wildcard syntax.
    const displayTerm = this.getDisplaySearchQuery(searchTerm);
    return this.sanitizeSearchTerm(displayTerm);
  }

  parseTitleScopedQuery(searchTerm) {
    // Normalize URLs generated by current and older versions of this search
    // fix, including mixed wildcard/unstarred title tokens.
    const matches = searchTerm.match(/title:"[^"]+"|title:[^\s]+/g);
    if (!matches?.length) return null;

    const remaining = searchTerm
      .replace(/title:"[^"]+"|title:[^\s]+/g, "")
      .trim();
    if (remaining.length) return null;

    return matches
      .map((part) =>
        part
          .replace(/^title:/, "")
          .replace(/^"|"$/g, "")
          .replace(/\*$/, ""),
      )
      .join(" ")
      .trim();
  }

  parseLegacyScopedQuery(searchTerm) {
    const prefix = '(title:"';
    const separator = '" OR sku:"';
    const suffix = '")';

    if (
      !searchTerm.startsWith(prefix) ||
      !searchTerm.endsWith(suffix) ||
      !searchTerm.includes(separator)
    ) {
      return null;
    }

    const queryBody = searchTerm.slice(prefix.length, -suffix.length);
    const separatorIndex = queryBody.indexOf(separator);
    if (separatorIndex === -1) return null;

    const titleTerm = queryBody.slice(0, separatorIndex);
    const skuTerm = queryBody.slice(separatorIndex + separator.length);

    return titleTerm === skuTerm ? titleTerm : null;
  }

  getDisplaySearchQuery(searchTerm) {
    return (
      this.parseTitleScopedQuery(searchTerm) ||
      this.parseLegacyScopedQuery(searchTerm) ||
      searchTerm
    );
  }

  isSearchResultsPage() {
    const searchPageUrl = new URL(
      this.dataset.searchPageUrl,
      window.location.origin,
    );

    return window.location.pathname === searchPageUrl.pathname;
  }

  buildSearchPageUrl(searchTerm, baseUrl = this.dataset.searchPageUrl) {
    const scopedQuery = this.buildTitleScopedQuery(searchTerm);
    if (!scopedQuery) return null;

    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set("q", scopedQuery);
    url.searchParams.set("type", "product");
    url.searchParams.set("options[prefix]", "last");
    url.searchParams.set("options[unavailable_products]", "last");
    url.searchParams.delete("page");

    return url;
  }

  updateSearchPageDisplay(scopedQuery, displayQuery) {
    this.input.value = displayQuery;

    document.querySelectorAll("search-tab[data-terms]").forEach((tab) => {
      tab.dataset.terms = displayQuery;
    });

    document.querySelectorAll(".search-results__terms").forEach((element) => {
      element.textContent = displayQuery;
    });

    document
      .querySelectorAll(".collection__counter p, .search-results__no-results")
      .forEach((element) => {
        if (element.textContent.includes(scopedQuery)) {
          element.textContent = element.textContent
            .split(scopedQuery)
            .join(displayQuery);
        }
      });

    this.syncExactSearchResultCount();
  }

  syncExactSearchResultCount() {
    const grid = document.querySelector("#product-grid");
    const countMarker = grid?.querySelector("[data-exact-search-result-count]");

    if (!grid || !countMarker) return;

    // A marker counts the currently rendered page. It is a global count only
    // when Shopify did not need pagination for the candidate query.
    if (grid.querySelector(".wt-pagination-wrapper")) return;

    const exactCount = Number(countMarker.dataset.exactSearchResultCount || 0);

    document.querySelectorAll(".collection__counter p").forEach((element) => {
      const countMatch = element.textContent.match(/\d[\d,]*/);
      if (!countMatch) return;

      element.textContent = element.textContent.replace(
        countMatch[0],
        String(exactCount),
      );
    });
  }

  normalizeSearchPageQuery() {
    if (!this.input || !this.isSearchResultsPage()) return false;

    const currentUrl = new URL(window.location.href);
    const rawQuery = (
      currentUrl.searchParams.get("q") || this.input.value || ""
    ).trim();

    if (!rawQuery) return false;

    const displayQuery = this.getDisplaySearchQuery(rawQuery);
    const scopedQuery = this.buildTitleScopedQuery(displayQuery);

    if (!scopedQuery) return false;

    const needsScopedReload =
      currentUrl.searchParams.get("q") !== scopedQuery ||
      currentUrl.searchParams.get("type") !== "product" ||
      currentUrl.searchParams.get("options[prefix]") !== "last" ||
      currentUrl.searchParams.get("options[unavailable_products]") !== "last";

    if (needsScopedReload) {
      const queryChanged = currentUrl.searchParams.get("q") !== scopedQuery;
      currentUrl.searchParams.set("q", scopedQuery);
      currentUrl.searchParams.set("type", "product");
      currentUrl.searchParams.set("options[prefix]", "last");
      currentUrl.searchParams.set("options[unavailable_products]", "last");
      if (queryChanged) currentUrl.searchParams.delete("page");
      window.history.replaceState({}, "", currentUrl.toString());
    }

    this.currentScopedQuery = scopedQuery;
    this.currentDisplayQuery = displayQuery;
    this.updateSearchPageDisplay(scopedQuery, displayQuery);
    return false;
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length) {
      this.getSearchResults(searchTerm);
      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  async onFormSubmit(event) {
    const currentQuery = this.getQuery();

    if (
      !currentQuery.length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    const displayQuery = this.getDisplaySearchQuery(currentQuery);
    const skuProductUrl = await this.findExactSkuProductUrl(displayQuery);

    if (skuProductUrl) {
      window.location.href = skuProductUrl;
      return;
    }

    const searchUrl = this.buildSearchPageUrl(displayQuery);
    if (searchUrl) {
      window.location.href = searchUrl.toString();
    }
  }

  async findExactSkuProductUrl(searchTerm) {
    const normalizedTerm = this.sanitizeSearchTerm(searchTerm);
    if (!normalizedTerm) return null;

    try {
      const section = await this.fetchPredictiveSection(
        normalizedTerm,
        "variants.sku",
      );

      return (
        section?.querySelector(
          '[data-exact-sku-match="true"][data-product-url]',
        )?.dataset.productUrl || null
      );
    } catch (error) {
      return null;
    }
  }

  async getExactSearchFallbackItems(searchTerm, existingUrls, limit) {
    if (limit <= 0) return [];

    const searchUrl = this.buildSearchPageUrl(searchTerm);
    if (!searchUrl) return [];

    try {
      const response = await fetch(searchUrl.toString(), {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok) return [];

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, "text/html");
      const items = [];

      for (const template of doc.querySelectorAll(
        "template[data-exact-search-suggestion]",
      )) {
        const productUrl = template.dataset.productUrl;
        if (!productUrl || existingUrls.has(productUrl)) continue;

        const item = template.content.firstElementChild?.cloneNode(true);
        if (!item) continue;

        existingUrls.add(productUrl);
        items.push(item);

        if (items.length >= limit) break;
      }

      return items;
    } catch (error) {
      return [];
    }
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    const requestId = ++this.searchRequestId;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      let section = await this.fetchPredictiveSection(searchTerm);
      let list = section.querySelector("#predictive-search-results-list");

      if (!list || list.children.length === 0) {
        const fallbackTerm = this.getPredictiveFallbackTerm(searchTerm);
        if (
          fallbackTerm &&
          fallbackTerm.toLowerCase() !== searchTerm.toLowerCase()
        ) {
          const fallbackSection = await this.fetchPredictiveSection(fallbackTerm);
          const fallbackList = fallbackSection.querySelector(
            "#predictive-search-results-list",
          );
          if (fallbackList?.children.length) {
            section = fallbackSection;
            list = fallbackList;
          }
        }
      }

      const exactSkuMatch = list?.querySelector(
        '[data-exact-sku-match="true"]',
      );

      if (list && !exactSkuMatch) {
        const existingUrls = new Set(
          Array.from(list.querySelectorAll("[data-product-url]"))
            .map((item) => item.dataset.productUrl)
            .filter(Boolean),
        );
        const needed = Math.max(0, 4 - list.children.length);

        if (needed > 0) {
          const fallbackItems = await this.getExactSearchFallbackItems(
            searchTerm,
            existingUrls,
            needed,
          );
          fallbackItems.forEach((item) => list.appendChild(item));

          if (list.children.length > 0) {
            section.querySelector(".search-result-products")?.removeAttribute("hidden");
            section
              .querySelector("#predictive-search-results")
              ?.classList.remove("search-result--no-results");
          }
        }
      }

      if (
        requestId !== this.searchRequestId ||
        this.getQuery() !== searchTerm
      ) {
        return;
      }

      const resultsMarkup = section.innerHTML;
      this.cachedResults[queryKey] = resultsMarkup;
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (
        requestId !== this.searchRequestId ||
        this.getQuery() !== searchTerm
      ) {
        return;
      }
      console.error("Predictive search failed:", error);
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText || "";

    this.timeOut = setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.setLiveRegionResults();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
  }

  clearResults() {
    this.input.value = "";
    this.removeAttribute("results");
    this.setAttribute("empty", true);
    this.statusElement.innerHTML = this.suggestionMenuContent.innerHTML || "";

    clearTimeout(this.timeOut);
    setTimeout(() => {
      this.statusElement.removeAttribute("aria-hidden");
    }, 300);
  }
}

customElements.define("search-drawer", DrawerSearchSection);
